/**
 * `RelayService` —— 中继连接编排：地址解析、连接/切换、心跳延迟、在线目录、流量统计（ARCH §3.8）。
 *
 * 延迟创建 `HybridTransport`：必须先解锁才能拿到 `peerSeed`（PeerID 由助记词派生）。
 */

import { buildEnvelope } from '@/crypto/envelope';
import { bytesToBase64 } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import { HEARTBEAT_INTERVAL_MS, resolveRelayAddr } from '@/config';
import { HybridTransport } from '@/net/HybridTransport';
import { DirectoryClient } from '@/net/directory';
import { TrafficMeter } from '@/net/traffic';
import type { WebRTCSignal } from '@/net/webrtc';
import { useNetworkStore } from '@/store/network';
import { useUiStore } from '@/store/ui';
import type { IStorage } from '@/storage/IStorage';
import type { DayTraffic, OnlinePeer, RelayEntry } from '@shared/types';
import type { IdentityService } from './IdentityService';
import type { ChatService } from './ChatService';

const logger = createLogger('service:relay');

/** settings 键（与 `store/settings.ts` 约定一致）。 */
const SETTINGS_STEALTH = 'stealth';
const SETTINGS_RELAY_ADDR = 'relayAddr';

export interface RelayServiceOptions {
  storage: IStorage;
  identity: IdentityService;
  /**
   * 聊天服务（用于中继**重连后补发**尚未握手的 intro）。
   * 构造顺序上 `container.chat` 晚于本服务（它依赖已装配的 transport），故亦可后置注入
   * ⋯{@link RelayService.setChat}。
   */
  chat?: ChatService;
  /** 仅测试用：替换内部 `HybridTransport` 的构造（默认走真实构造）。 */
  transportFactory?: () => HybridTransport;
}

export class RelayService {
  private transport: HybridTransport | null = null;
  private directory: DirectoryClient | null = null;
  private readonly traffic: TrafficMeter;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  /** 聊天服务引用（后置注入，用于重连补发 intro）。 */
  private chat: ChatService | null = null;

  constructor(private readonly opts: RelayServiceOptions) {
    this.traffic = new TrafficMeter(opts.storage.traffic);
    this.chat = opts.chat ?? null;
  }

  /** 后置注入 `ChatService`（`container.chat` 依赖已装配的 transport，构造期尚不可用）。 */
  setChat(chat: ChatService): void {
    this.chat = chat;
  }

  // ─────────────── 生命周期 ───────────────

  /** 解析地址 → 建传输 → 起心跳。重复调用幂等。 */
  async start(): Promise<HybridTransport> {
    const transport = await this.ensureTransport();
    if (this.running) return transport;
    this.running = true;

    const addr = await this.pickRelayAddr();
    useNetworkStore.getState().setRelay({ addr, status: 'connecting' });
    await transport.start(addr);

    this.startHeartbeat();
    await this.applyVisibility();
    logger.info('中继服务已启动', addr);
    return transport;
  }

  stop(): void {
    this.running = false;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    void this.traffic.flush(true);
    void this.transport?.stop();
    useNetworkStore.getState().setRelay({ status: 'disconnected' });
  }

  /** 解锁后调用：构造（或复用）传输层。 */
  async ensureTransport(): Promise<HybridTransport> {
    if (this.transport) return this.transport;
    const keys = this.opts.identity.requireUnlocked();

    const transport = this.opts.transportFactory?.() ?? new HybridTransport({
      peerSeed: new Uint8Array(keys.peerSeed),
      traffic: this.traffic,
      onOutgoingSignal: (to, signal) => this.sendSignalEnvelope(to, signal),
      onRelayStatus: (status, addr) => {
        useNetworkStore.getState().setRelay({ status, addr: addr ?? undefined });
        if (status === 'ready') void this.refreshOnline();
      },
      onRelayReady: (addr, selfPeerId) => this.handleRelayReady(addr, selfPeerId),
    });

    transport.onStatus((peerId, status, latencyMs) => {
      useNetworkStore.getState().setPeerStatus(peerId, status, latencyMs);
    });

    this.transport = transport;
    this.directory = new DirectoryClient(() => this.transport?.getPeerClient() ?? null);
    return transport;
  }

  getTransport(): HybridTransport {
    if (!this.transport) throw new MeshLinkError('E_RELAY_UNAVAILABLE', '传输层尚未创建（请先解锁）');
    return this.transport;
  }

  // ─────────────── 能力 ───────────────

  /** 拉在线目录。 */
  async refreshOnline(): Promise<OnlinePeer[]> {
    if (!this.directory) return [];
    const peers = await this.directory.onlinePeers();
    useNetworkStore.getState().setOnline(peers);
    return peers;
  }

  /** 拉中继目录（切换中继用）。 */
  async listRelays(): Promise<RelayEntry[]> {
    if (!this.directory) return [];
    return this.directory.relays();
  }

  /** 手动切换中继（失败由 RelayConnectionManager 退避重试）。 */
  async switchRelay(): Promise<boolean> {
    if (!this.transport) return false;
    const ok = await this.transport.switchRelay();
    if (!ok) {
      // 中继全不可用属「链路级/需持续提示 + 提供切换动作」的驻留态 → 走 ErrorBanner（不再弹瞬态 Toast）。
      useUiStore.getState().pushError('E_RELAY_UNAVAILABLE', { global: true });
      logger.warn('切换中继失败，已保留原中继并转入退避重连');
    }
    return ok;
  }

  /** 隐身开关：false 时不上报进目录，他人在线列表里看不到自己。 */
  async setStealth(stealth: boolean): Promise<void> {
    await this.opts.storage.settings.set(SETTINGS_STEALTH, stealth);
    useNetworkStore.getState().setStealth(stealth);
    await this.applyVisibility();
  }

  /** 今日流量。 */
  async todayTraffic(): Promise<DayTraffic> {
    return this.traffic.today();
  }

  /** 立即拉一次离线邮箱。 */
  async pollMailboxNow(): Promise<number> {
    if (!this.transport) return 0;
    return this.transport.pollMailboxNow();
  }

  /**
   * 中继（含**重连**）就绪后的业务动作：刷新在线状态 + **补发尚未握手的 intro**。
   *
   * 之所以要补发：`sendRaw` 静默丢包（`HybridTransport.sendRaw` 未就绪直接 return、失败不重试），
   * 若断连恰落在 intro 握手窗口，intro 会永久丢失 → 联系人永久 pending。`afterConnected()`
   * （`HybridTransport.ts:264-274`）在**每次连接成功（含重连）**都会回调本方法。
   */
  handleRelayReady(addr: string, selfPeerId: string): void {
    useNetworkStore.getState().setRelay({ addr, peerId: selfPeerId, status: 'ready' });
    // 中继（含重连）就绪 ⇒ 链路恢复，清掉驻留的链路级错误横幅（E_RELAY_UNAVAILABLE 等）。
    useUiStore.getState().clearGlobalError();
    void this.applyVisibility();
    void this.chat?.resendPendingIntros();
  }

  // ─────────────── 内部 ───────────────

  private async pickRelayAddr(): Promise<string> {
    const override = await this.opts.storage.settings.get(SETTINGS_RELAY_ADDR);
    if (typeof override === 'string' && override.length > 0) return override;
    const info = await resolveRelayAddr();
    return info.addr;
  }

  private async applyVisibility(): Promise<void> {
    // 未装配（`ensureTransport` 未跑 / 无目录客户端）时直接返回：既无可上报的 peer，
    // 也避免在依赖尚未就绪时读取 storage（既有单测以 `{}` 桩 storage 调 `handleRelayReady`）。
    if (!this.directory) return;
    const stealthSetting = await this.opts.storage.settings.get(SETTINGS_STEALTH);
    const stealth = stealthSetting === true;
    useNetworkStore.getState().setStealth(stealth);
    // 中继未就绪时不必、也发不出去：`transport.start()` 是「发起连接」而非「等待就绪」，
    // 故 `start()` 里那次调用必然早于就绪。真正的上报由 `handleRelayReady()` 在
    // **每次连接就绪（含重连）**时触发 —— 重连后服务端已清掉本端记录，必须重新上报。
    if (!this.transport?.isRelayReady) return;
    try {
      await this.directory.setVisible(!stealth, this.opts.identity.current?.name ?? '');
    } catch (err) {
      logger.debug('可见性上报失败（中继可能未就绪）', err instanceof Error ? err.message : err);
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      void (async () => {
        if (!this.transport) return;
        // 一次目录查询同时得在线列表与往返时延：
        // 此前 measureLatency() 与 refreshOnline() 各发一次同构 list 请求 → 每 tick 双倍目录流量。
        const { peers, latencyMs } = await this.transport.fetchOnlineWithLatency();
        useNetworkStore.getState().setRelay({ latencyMs: latencyMs ?? undefined });
        useNetworkStore.getState().setOnline(peers);
      })();
    }, HEARTBEAT_INTERVAL_MS);
  }

  /** WebRTC 打洞信令：包成 `kind:'signal'` 信封后走 `sendRaw`（不进 ACK 流程）。 */
  private sendSignalEnvelope(to: string, signal: WebRTCSignal): void {
    if (!this.transport) return;
    try {
      const keys = this.opts.identity.requireUnlocked();
      const envelope = buildEnvelope({
        kind: 'signal',
        from: this.opts.identity.did,
        to,
        signSeed: keys.signSeed,
        encPub: bytesToBase64(keys.encPub),
        sequence: 0,
        type: signal.type,
        sdp: signal.sdp,
        candidate: signal.candidate,
        sdpMid: signal.sdpMid,
        sdpMLineIndex: signal.sdpMLineIndex,
      });
      this.transport.sendRaw(to, envelope);
    } catch (err) {
      logger.debug('信令发送失败', err instanceof Error ? err.message : err);
    }
  }
}

export default RelayService;
